/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * FIN VARIANT PICKER V4 - EXTERNAL JAVASCRIPT
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * @version 4.0.0
 * @updated 2025-12-28
 * @description AJAX-based variant picker for Select Fin products.
 * @features Caching, prefetching, optimistic UI updates, error handling, accessibility.
 * @dependencies fin-variant-picker-v4.css, variation_data metafield
 */

(function () {
  "use strict";

  // ═══════════════════════════════════════════════════════════════════════════════
  // CONFIGURATION & UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Development mode detection
   * @type {boolean}
   */
  const isDevelopment = window.location.hostname === "localhost" || window.location.hostname.includes(".myshopify.com");

  /**
   * Conditional logging helper (development only)
   * @param {...any} args - Arguments to log
   */
  function log(...args) {
    if (isDevelopment) {
    }
  }

  /**
   * Always log errors (even in production)
   * @param {...any} args - Arguments to log
   */
  function logError(...args) {
    console.error(...args);
  }

  /**
   * Always log warnings (even in production)
   * @param {...any} args - Arguments to log
   */
  function logWarn(...args) {
    console.warn(...args);
  }

  /**
   * Check if user prefers reduced motion
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // PERFORMANCE ENHANCEMENTS: Caching & Prefetching
  // ═══════════════════════════════════════════════════════════════════════════════

  /** @type {Map<string, {data: any, timestamp: number}>} */
  const productCache = new Map();
  /** @type {Set<string>} */
  const prefetchQueue = new Set();
  /** @type {boolean} */
  let navigationInProgress = false;
  /** @type {AbortController|null} */
  let currentController = null;

  /** Cache TTL: 5 minutes */
  const CACHE_TTL = 5 * 60 * 1000;
  /** Maximum cache size: 50 entries */
  const MAX_CACHE_SIZE = 50;
  /** Availability cache for products */
  const availabilityCache = new Map();

  /**
   * Get cached product data if available and not expired
   * @param {string} handle - Product handle
   * @returns {any|null} Cached data or null
   */
  function getCachedProduct(handle) {
    const cached = productCache.get(handle);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    // Remove expired entry
    if (cached) {
      productCache.delete(handle);
    }
    return null;
  }

  /**
   * Cache product data with LRU eviction
   * @param {string} handle - Product handle
   * @param {any} data - Data to cache
   */
  function setCachedProduct(handle, data) {
    // Evict oldest if at limit
    if (productCache.size >= MAX_CACHE_SIZE) {
      const entries = Array.from(productCache.entries());
      const oldest = entries.sort((a, b) => a[1].timestamp - b[1].timestamp)[0];
      productCache.delete(oldest[0]);
      log("FinPicker V4: Cache evicted oldest entry:", oldest[0]);
    }

    productCache.set(handle, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Get section ID from DOM
   * @returns {string} Section ID
   */
  function getSectionId() {
    const picker = document.getElementById("finPickerV4");
    const section = picker?.closest('[id^="shopify-section-"]');
    return section ? section.id.replace("shopify-section-", "") : "main";
  }

  /**
   * Extract picker data from section HTML
   * @param {Element} section - Section element
   * @returns {any|null} Parsed picker data or null
   */
  function extractPickerData(section) {
    const picker = section.querySelector("#finPickerV4");
    if (!picker) return null;

    const dataEl = picker.querySelector("#finVariationDataV4");
    if (!dataEl) return null;

    try {
      return JSON.parse(dataEl.textContent.trim());
    } catch (e) {
      logError("FinPicker V4: Failed to extract picker data", e);
      return null;
    }
  }

  /**
   * Prefetch product data on hover
   * @param {string} handle - Product handle
   */
  function prefetchProduct(handle) {
    if (prefetchQueue.has(handle) || productCache.has(handle)) {
      return;
    }

    prefetchQueue.add(handle);
    const productUrl = `/products/${handle}`;
    const sectionUrl = `${productUrl}?section_id=${getSectionId()}`;

    fetch(sectionUrl, { priority: "low" })
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const sectionId = getSectionId();
        const newSection = doc.querySelector(`#shopify-section-${sectionId}`);

        if (newSection) {
          const pickerData = extractPickerData(newSection);
          if (pickerData) {
            setCachedProduct(handle, {
              html: newSection.outerHTML,
              pickerData: pickerData,
              title: doc.querySelector("title")?.textContent,
            });
          }
        }
      })
      .catch((err) => logWarn("FinPicker V4: Prefetch failed for", handle, err))
      .finally(() => prefetchQueue.delete(handle));
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // AVAILABILITY FETCHING
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Fetch product availability from Shopify's product.js endpoint
   * @param {string} handle - Product handle
   * @returns {Promise<boolean>} - Whether product is available
   */
  async function fetchProductAvailability(handle) {
    // Check cache first
    const cached = availabilityCache.get(handle);
    if (cached !== undefined && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.available;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const product = await response.json();
      const available = product.available === true;

      // Cache the result
      availabilityCache.set(handle, {
        available,
        timestamp: Date.now(),
      });

      return available;
    } catch (error) {
      logWarn("FinPicker V4: Failed to fetch availability for", handle, error);
      return true; // Assume available on error to avoid blocking sales
    }
  }

  /**
   * Fetch availability for all products in a box type
   * @param {Object} boxData - Box data containing products
   * @returns {Promise<Object>} - Map of size to availability
   */
  async function fetchBoxAvailability(boxData) {
    if (!boxData || !boxData.products) {
      return {};
    }

    const availabilityMap = {};
    const fetchPromises = [];

    for (const [size, productData] of Object.entries(boxData.products)) {
      if (productData && productData.handle) {
        fetchPromises.push(
          fetchProductAvailability(productData.handle).then((available) => {
            availabilityMap[size] = available;
          }),
        );
      }
    }

    // Fetch all in parallel
    await Promise.all(fetchPromises);

    return availabilityMap;
  }

  /**
   * Update boxes data with availability information
   * @param {Object} boxes - All boxes data
   * @param {string} currentBox - Current box type to prioritize
   * @returns {Promise<Object>} - Updated boxes with availability
   */
  async function enrichBoxesWithAvailability(boxes, currentBox) {
    if (!boxes || typeof boxes !== "object") {
      return boxes;
    }

    // Fetch availability for current box first (priority)
    if (currentBox && boxes[currentBox]) {
      const availabilityMap = await fetchBoxAvailability(boxes[currentBox]);

      // Update product data with availability
      for (const [size, available] of Object.entries(availabilityMap)) {
        if (boxes[currentBox].products && boxes[currentBox].products[size]) {
          boxes[currentBox].products[size].available = available;
        }
      }
    }

    // Fetch availability for other boxes in background (non-blocking)
    for (const [boxName, boxData] of Object.entries(boxes)) {
      if (boxName !== currentBox) {
        fetchBoxAvailability(boxData).then((availabilityMap) => {
          for (const [size, available] of Object.entries(availabilityMap)) {
            if (boxes[boxName].products && boxes[boxName].products[size]) {
              boxes[boxName].products[size].available = available;
            }
          }
        });
      }
    }

    return boxes;
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // ERROR HANDLING
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Show error state to user
   * @param {string} message - Error message
   * @param {Function} [retryCallback] - Optional retry function
   */
  function showErrorState(message, retryCallback) {
    const picker = document.getElementById("finPickerV4");
    if (!picker) return;

    // Remove existing error
    const existingError = picker.querySelector(".fpv4__error");
    if (existingError) {
      existingError.remove();
    }

    // Create error element
    const errorEl = document.createElement("div");
    errorEl.className = "fpv4__error";
    errorEl.setAttribute("role", "alert");
    errorEl.setAttribute("aria-live", "assertive");
    errorEl.innerHTML = `
      <div class="fpv4__error-content">
        <p class="fpv4__error-message">${escapeHtml(message)}</p>
        ${retryCallback ? `<button type="button" class="fpv4__error-retry" aria-label="Retry">Try Again</button>` : ""}
        <button type="button" class="fpv4__error-close" aria-label="Close error">×</button>
      </div>
    `;

    // Insert at top of picker
    picker.insertBefore(errorEl, picker.firstChild);

    // Bind retry button
    if (retryCallback) {
      const retryBtn = errorEl.querySelector(".fpv4__error-retry");
      if (retryBtn) {
        retryBtn.addEventListener("click", () => {
          errorEl.remove();
          retryCallback();
        });
      }
    }

    // Bind close button
    const closeBtn = errorEl.querySelector(".fpv4__error-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        errorEl.remove();
      });
    }

    // Focus on error for accessibility
    errorEl.focus();
  }

  /**
   * Hide error state
   */
  function hideErrorState() {
    const picker = document.getElementById("finPickerV4");
    if (!picker) return;

    const errorEl = picker.querySelector(".fpv4__error");
    if (errorEl) {
      errorEl.remove();
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Announce changes to screen readers
   * @param {string} message - Message to announce
   */
  function announceToScreenReader(message) {
    const announcer = document.getElementById("fpv4-announcer");
    if (!announcer) return;

    // Clear then set to trigger announcement
    announcer.textContent = "";

    // Use requestAnimationFrame to ensure DOM update
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════════

  // Lazy initialization with Intersection Observer
  document.addEventListener("DOMContentLoaded", () => {
    const pickerElement = document.getElementById("finPickerV4");

    if (!pickerElement) return;

    // Check if element is already visible (in viewport or close to it)
    function isElementVisible(element) {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      // Check if element is within viewport or within 200px above/below
      return rect.top < windowHeight + 200 && rect.bottom > -200;
    }

    // If element is already visible, initialize immediately
    if (isElementVisible(pickerElement)) {
      initFinPickerV4();
      return;
    }

    // Otherwise, use Intersection Observer for lazy loading
    if ("IntersectionObserver" in window) {
      let initialized = false;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !initialized) {
              initialized = true;
              // Initialize picker when visible
              initFinPickerV4();
              // Stop observing after init
              observer.disconnect();
            }
          });
        },
        {
          // Start loading slightly before element is visible
          rootMargin: "100px 0px",
          threshold: 0,
        },
      );

      observer.observe(pickerElement);

      // Safety timeout: initialize after 2 seconds if observer hasn't fired
      setTimeout(() => {
        if (!initialized) {
          initialized = true;
          observer.disconnect();
          initFinPickerV4();
        }
      }, 2000);
    } else {
      // Fallback for older browsers
      initFinPickerV4();
    }
  });

  /**
   * Initialize the fin picker
   */
  function initFinPickerV4() {
    const picker = document.getElementById("finPickerV4");
    if (!picker) {
      logWarn("FinPicker V4: Picker element not found");
      return;
    }

    // Ensure picker and parent containers are visible
    picker.style.display = "flex";
    picker.style.visibility = "visible";
    picker.style.opacity = "1";

    // Ensure parent container is visible
    const parentLiquid = picker.closest(".product-info__liquid");
    if (parentLiquid) {
      parentLiquid.style.display = "block";
      parentLiquid.style.visibility = "visible";
      parentLiquid.style.opacity = "1";
      parentLiquid.style.overflow = "visible";
    }

    // Ensure parent block item is visible
    const parentBlock = picker.closest(".product-info__block-item");
    if (parentBlock) {
      parentBlock.style.display = "block";
      parentBlock.style.visibility = "visible";
      parentBlock.style.opacity = "1";
    }

    const dataEl = document.getElementById("finVariationDataV4");
    if (!dataEl) {
      logWarn("FinPicker V4: Data element not found");
      showErrorState("Unable to load product variations. Please refresh the page.");
      return;
    }

    // Get and trim the raw text content
    const rawData = dataEl.textContent.trim();

    // Log raw data for debugging (development only)
    if (rawData.length > 0) {
      log("FinPicker V4: Raw data preview:", rawData.substring(0, 200) + (rawData.length > 200 ? "..." : ""));
    } else {
      logWarn("FinPicker V4: Empty data element");
      showErrorState("Product variation data is missing. Please refresh the page.");
      return;
    }

    let data;
    try {
      // Validate that we have content before parsing
      if (!rawData || rawData.length === 0) {
        throw new Error("Empty JSON string");
      }

      data = JSON.parse(rawData);
    } catch (e) {
      logError("FinPicker V4: Failed to parse JSON data", e);
      showErrorState("Unable to parse product data. Please refresh the page.", () => {
        window.location.reload();
      });
      return;
    }

    // Validate parsed data structure
    if (!data || typeof data !== "object") {
      logError("FinPicker V4: Parsed data is not an object", data);
      showErrorState("Invalid product data format. Please refresh the page.");
      return;
    }

    const { currentBox, currentSize, boxes } = data;

    if (!boxes || typeof boxes !== "object" || Object.keys(boxes).length === 0) {
      logWarn("FinPicker V4: No variation data available (empty boxes object)");
      showErrorState("No product variations available for this product.");
      return;
    }

    // Validate boxes structure
    const validBoxes = {};
    let hasValidData = false;

    for (const [boxName, boxData] of Object.entries(boxes)) {
      if (boxData && typeof boxData === "object" && Array.isArray(boxData.sizes) && boxData.sizes.length > 0) {
        validBoxes[boxName] = boxData;
        hasValidData = true;
      } else {
        logWarn(`FinPicker V4: Invalid box data for "${boxName}"`, boxData);
      }
    }

    if (!hasValidData) {
      logError("FinPicker V4: No valid box data found");
      showErrorState("No valid product variations available. Please refresh the page.");
      return;
    }

    // Hide any existing errors
    hideErrorState();

    // Hide loading indicators
    const loadingIndicators = picker.querySelectorAll(".fpv4__loading-indicator");
    loadingIndicators.forEach((indicator) => {
      indicator.style.display = "none";
    });

    // Render initial content immediately (without availability)
    renderBoxPills(validBoxes, currentBox, currentSize);
    renderSizeGrid(validBoxes, currentBox, currentSize);

    // Fetch availability and re-render with correct OOS status
    enrichBoxesWithAvailability(validBoxes, currentBox).then((enrichedBoxes) => {
      log("FinPicker V4: Availability data loaded, re-rendering size grid");
      renderSizeGrid(enrichedBoxes, currentBox, currentSize);
    });

    // Verify content was rendered
    const boxPillsContainer = document.getElementById("boxPillsV4");
    const sizeGridContainer = document.getElementById("sizeGridV4");

    if (boxPillsContainer && boxPillsContainer.children.length === 0) {
      logWarn("FinPicker V4: Box pills container is empty after rendering");
      // Show loading indicator as fallback
      const loadingIndicator = boxPillsContainer.querySelector(".fpv4__loading-indicator");
      if (loadingIndicator) {
        loadingIndicator.style.display = "block";
      }
    }

    if (sizeGridContainer && sizeGridContainer.children.length === 0) {
      logWarn("FinPicker V4: Size grid container is empty after rendering");
      // Show loading indicator as fallback
      const loadingIndicator = sizeGridContainer.querySelector(".fpv4__loading-indicator");
      if (loadingIndicator) {
        loadingIndicator.style.display = "block";
      }
    }

    // Ensure picker has visible content
    const hasContent =
      (boxPillsContainer && boxPillsContainer.children.length > 0) ||
      (sizeGridContainer && sizeGridContainer.children.length > 0);

    if (!hasContent) {
      logError("FinPicker V4: No content rendered");
      showErrorState("Unable to render product variations. Please refresh the page.");
      return;
    }

    log("FinPicker V4: Successfully initialized with", Object.keys(validBoxes).length, "box types");

    // Bind click handlers for dynamic navigation
    bindNavigationHandlers();

    // Bind keyboard handlers
    bindKeyboardHandlers();

    // Setup prefetching on hover
    setupPrefetching(validBoxes);

    // Setup modal handlers
    setupModalHandlers();
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // PREFETCHING: Setup hover prefetch
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Setup prefetching on hover
   * @param {Object} boxes - Boxes data
   */
  function setupPrefetching(boxes) {
    const picker = document.getElementById("finPickerV4");
    if (!picker) return;
    if (picker.dataset.prefetchBound === "true") return;
    picker.dataset.prefetchBound = "true";

    // Prefetch on hover with debounce
    let hoverTimer = null;

    picker.addEventListener(
      "mouseenter",
      (e) => {
        const pill = e.target.closest(".fpv4__pill[data-handle]");
        const sizeBtn = e.target.closest(".fpv4__size[data-handle]");

        const handle = pill?.dataset.handle || sizeBtn?.dataset.handle;
        if (handle) {
          hoverTimer = setTimeout(() => {
            prefetchProduct(handle);
          }, 300); // Prefetch after 300ms hover
        }
      },
      true,
    );

    picker.addEventListener(
      "mouseleave",
      () => {
        if (hoverTimer) {
          clearTimeout(hoverTimer);
          hoverTimer = null;
        }
      },
      true,
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // MODAL HANDLING
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Setup modal handlers with safety checks
   */
  function setupModalHandlers() {
    const picker = document.getElementById("finPickerV4");
    if (!picker) return;

    // Bind "Unknown Fin Box?" help button (re-bound on each AJAX nav since innerHTML is replaced)
    const boxHelpBtns = picker.querySelectorAll(".box-help-link");
    boxHelpBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        if (typeof openFinBoxModal === "function") {
          openFinBoxModal();
        } else {
          logWarn("FinPicker V4: openFinBoxModal function not available");
        }
      });
    });

    // Legacy: find help button using inline onclick and replace with event listener
    const helpBtn = picker.querySelector(".fpv4__help[onclick]");
    if (helpBtn) {
      helpBtn.removeAttribute("onclick");
      helpBtn.addEventListener("click", (e) => {
        e.preventDefault();
        if (typeof openFinBoxModal === "function") {
          openFinBoxModal();
        } else {
          logWarn("FinPicker V4: openFinBoxModal function not available");
          const sizingSection = document.getElementById("sizing");
          if (sizingSection) sizingSection.scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // KEYBOARD NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Bind keyboard navigation handlers
   */
  function bindKeyboardHandlers() {
    const picker = document.getElementById("finPickerV4");
    if (!picker) return;

    // Box pills keyboard navigation
    const pillsContainer = picker.querySelector("#boxPillsV4");
    if (pillsContainer) {
      pillsContainer.addEventListener("keydown", (e) => {
        const pills = Array.from(pillsContainer.querySelectorAll(".fpv4__pill[data-handle]"));
        const currentIndex = pills.indexOf(document.activeElement);

        if (currentIndex === -1) return;

        let newIndex = currentIndex;

        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          newIndex = (currentIndex + 1) % pills.length;
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          newIndex = (currentIndex - 1 + pills.length) % pills.length;
        } else if (e.key === "Home") {
          e.preventDefault();
          newIndex = 0;
        } else if (e.key === "End") {
          e.preventDefault();
          newIndex = pills.length - 1;
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          pills[currentIndex].click();
          return;
        }

        if (newIndex !== currentIndex) {
          pills[newIndex].focus();
        }
      });
    }

    // Size grid keyboard navigation
    const sizesContainer = picker.querySelector("#sizeGridV4");
    if (sizesContainer) {
      sizesContainer.addEventListener("keydown", (e) => {
        const sizes = Array.from(sizesContainer.querySelectorAll(".fpv4__size[data-handle]"));
        const currentIndex = sizes.indexOf(document.activeElement);

        if (currentIndex === -1) return;

        const cols = window.innerWidth <= 599 ? 4 : 6;
        let newIndex = currentIndex;

        if (e.key === "ArrowRight") {
          e.preventDefault();
          newIndex = Math.min(currentIndex + 1, sizes.length - 1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          newIndex = Math.max(currentIndex - 1, 0);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          newIndex = Math.min(currentIndex + cols, sizes.length - 1);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          newIndex = Math.max(currentIndex - cols, 0);
        } else if (e.key === "Home") {
          e.preventDefault();
          newIndex = 0;
        } else if (e.key === "End") {
          e.preventDefault();
          newIndex = sizes.length - 1;
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          sizes[currentIndex].click();
          return;
        }

        if (newIndex !== currentIndex) {
          sizes[newIndex].focus();
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // OPTIMISTIC UI UPDATE: Update picker immediately
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Update picker UI optimistically before server response
   * @param {string} newBox - New box type
   * @param {number} newSize - New size
   * @param {string} newPrice - New price
   * @param {boolean} newStock - New stock status
   */
  function updatePickerOptimistically(newBox, newSize, newPrice, newStock) {
    const picker = document.getElementById("finPickerV4");
    if (!picker) return;

    // Update active states immediately
    const allPills = picker.querySelectorAll(".fpv4__pill");
    allPills.forEach((pill) => {
      pill.classList.remove("fpv4__pill--active");
      pill.setAttribute("aria-checked", "false");
      if (pill.dataset.box === newBox) {
        pill.classList.add("fpv4__pill--active");
        pill.setAttribute("aria-checked", "true");
      }
    });

    const allSizes = picker.querySelectorAll(".fpv4__size");
    allSizes.forEach((size) => {
      size.classList.remove("fpv4__size--active");
      if (parseInt(size.dataset.size) === newSize) {
        size.classList.add("fpv4__size--active");
      }
    });

    // Update price and stock with aria-live announcement
    const priceEl = document.getElementById("priceDisplayV4");
    const priceLive = picker.querySelector(".fpv4__price-live");
    if (priceEl && newPrice) {
      priceEl.textContent = newPrice;
      if (priceLive) {
        priceLive.textContent = `Price updated to ${newPrice}`;
      }
    }

    const stockEl = picker.querySelector(".fpv4__stock");
    if (stockEl && newStock !== undefined) {
      stockEl.innerHTML = newStock
        ? '<span class="fpv4__stock--in"><span class="fpv4__stock-dot"></span>In Stock — Ready to Ship</span>'
        : '<span class="fpv4__stock--out"><span class="fpv4__stock-dot fpv4__stock-dot--out"></span>Out of Stock</span>';
    }

    // Update selected values
    const selectedBox = document.getElementById("selectedBoxV4");
    const selectedSize = document.getElementById("selectedSizeV4");
    if (selectedBox) selectedBox.textContent = newBox;
    if (selectedSize) selectedSize.textContent = `${newSize}cm`;

    // Maintain focus on active element
    const activePill = picker.querySelector(".fpv4__pill--active");
    const activeSize = picker.querySelector(".fpv4__size--active");
    if (document.activeElement && picker.contains(document.activeElement)) {
      // Keep focus on current element if it's still in picker
      if (activePill && document.activeElement.classList.contains("fpv4__pill")) {
        activePill.focus();
      } else if (activeSize && document.activeElement.classList.contains("fpv4__size")) {
        activeSize.focus();
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDERING
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Render box type pills
   * @param {Object} boxes - Boxes data
   * @param {string} currentBox - Current box type
   * @param {number} currentSize - Current size
   */
  function renderBoxPills(boxes, currentBox, currentSize) {
    const container = document.getElementById("boxPillsV4");
    if (!container) {
      logWarn("FinPicker V4: Box pills container not found");
      return;
    }

    if (!boxes || typeof boxes !== "object" || Object.keys(boxes).length === 0) {
      logWarn("FinPicker V4: No boxes data provided to renderBoxPills");
      container.innerHTML =
        '<span class="fpv4__pill" role="radio" aria-checked="false" tabindex="-1">No box types available</span>';
      return;
    }

    // Sort boxes
    const boxOrder = ["Tuttle", "Powerbox", "Deep Tuttle", "US Box", "Slotbox", "Mini Tuttle Box", "Trim Box"];
    const sortedBoxes = Object.keys(boxes).sort((a, b) => {
      const aIdx = boxOrder.indexOf(a);
      const bIdx = boxOrder.indexOf(b);
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });

    if (sortedBoxes.length === 0) {
      logWarn("FinPicker V4: No sorted boxes to render");
      container.innerHTML =
        '<span class="fpv4__pill" role="radio" aria-checked="false" tabindex="-1">No box types available</span>';
      return;
    }

    container.innerHTML = sortedBoxes
      .map((box, index) => {
        const boxData = boxes[box];
        if (!boxData || !Array.isArray(boxData.sizes)) {
          logWarn(`FinPicker V4: Invalid box data for "${box}"`);
          return "";
        }

        const isActive = box === currentBox;
        const count = boxData.sizes.length;

        if (isActive) {
          return `<span class="fpv4__pill fpv4__pill--active" role="radio" aria-checked="true" aria-label="${escapeHtml(box)} fin box, ${count} sizes available" tabindex="0">${escapeHtml(box)} <span class="fpv4__pill-count">(${count})</span></span>`;
        }

        // Find product with same size in this box type, or first available
        let targetHandle;
        if (boxData.products && boxData.products[String(currentSize)]) {
          targetHandle = boxData.products[String(currentSize)].handle;
        } else if (boxData.products && boxData.sizes.length > 0) {
          // Get first available size
          const firstSize = boxData.sizes[0];
          targetHandle = boxData.products[String(firstSize)]?.handle;
        }

        // Use button for dynamic navigation instead of link
        if (targetHandle) {
          return `<button type="button" class="fpv4__pill" role="radio" aria-checked="false" aria-label="${escapeHtml(box)} fin box, ${count} sizes available" data-box="${escapeHtml(box)}" data-handle="${escapeHtml(targetHandle)}" tabindex="${index === 0 ? "0" : "-1"}">${escapeHtml(box)} <span class="fpv4__pill-count">(${count})</span></button>`;
        }

        return `<span class="fpv4__pill" role="radio" aria-checked="false" aria-label="${escapeHtml(box)} fin box, ${count} sizes available" tabindex="-1">${escapeHtml(box)} <span class="fpv4__pill-count">(${count})</span></span>`;
      })
      .filter((html) => html.length > 0)
      .join("");

    log("FinPicker V4: Rendered", sortedBoxes.length, "box pills");
  }

  /**
   * Render size grid
   * @param {Object} boxes - Boxes data
   * @param {string} currentBox - Current box type
   * @param {number} currentSize - Current size
   */
  function renderSizeGrid(boxes, currentBox, currentSize) {
    const container = document.getElementById("sizeGridV4");
    if (!container) {
      logWarn("FinPicker V4: Size grid container not found");
      return;
    }

    if (!boxes || typeof boxes !== "object") {
      logWarn("FinPicker V4: Invalid boxes data in renderSizeGrid");
      container.innerHTML = `<span class="fpv4__size fpv4__size--active" role="button" aria-label="Size ${currentSize} cm, currently selected">${currentSize}</span>`;
      return;
    }

    const boxData = boxes[currentBox];
    if (!boxData) {
      logWarn(`FinPicker V4: No box data found for "${currentBox}"`);
      container.innerHTML = `<span class="fpv4__size fpv4__size--active" role="button" aria-label="Size ${currentSize} cm, currently selected">${currentSize}</span>`;
      return;
    }

    const { sizes, products } = boxData;

    if (!Array.isArray(sizes) || sizes.length === 0) {
      logWarn(`FinPicker V4: No sizes available for box "${currentBox}"`);
      container.innerHTML = `<span class="fpv4__size fpv4__size--active" role="button" aria-label="Size ${currentSize} cm, currently selected">${currentSize}</span>`;
      return;
    }

    if (!products || typeof products !== "object") {
      logWarn(`FinPicker V4: No products data for box "${currentBox}"`);
    }

    container.innerHTML = sizes
      .map((size, index) => {
        const isActive = size === currentSize;
        const productData = products && products[String(size)];

        // Cas 1: Pas de produit disponible
        if (!productData) {
          return `<span class="fpv4__size fpv4__size--oos" role="button" aria-label="Size ${size} cm, not available" aria-disabled="true" tabindex="-1">${size}</span>`;
        }

        // Cas 2: Produit out of stock (available === false ou 0)
        const isOutOfStock = productData.available === false || productData.available === 0;

        if (isActive) {
          return `<span class="fpv4__size fpv4__size--active" role="button" aria-label="Size ${size} cm, currently selected" aria-pressed="true" tabindex="0">${size}</span>`;
        }

        // Si out of stock, utiliser un bouton cliquable pour permettre la souscription back-in-stock
        if (isOutOfStock) {
          if (productData.handle) {
            return `<button type="button" class="fpv4__size fpv4__size--oos" role="button" aria-label="Size ${size} cm, out of stock - click to subscribe for restock notification" data-handle="${escapeHtml(productData.handle)}" data-size="${size}" data-oos="true" tabindex="0">${size}</button>`;
          }
          return `<span class="fpv4__size fpv4__size--oos" role="button" aria-label="Size ${size} cm, out of stock" aria-disabled="true" tabindex="-1">${size}</span>`;
        }

        // Produit disponible - utiliser un bouton pour navigation dynamique
        if (!productData.handle) {
          logWarn(`FinPicker V4: Product data for size ${size} missing handle`);
          return `<span class="fpv4__size fpv4__size--oos" role="button" aria-label="Size ${size} cm, not available" aria-disabled="true" tabindex="-1">${size}</span>`;
        }

        return `<button type="button" class="fpv4__size" role="button" aria-label="Size ${size} cm, available" data-handle="${escapeHtml(productData.handle)}" data-size="${size}" tabindex="${index === 0 ? "0" : "-1"}">${size}</button>`;
      })
      .join("");

    log("FinPicker V4: Rendered", sizes.length, "size buttons for box", currentBox);

    // Announce to screen readers when box type changes
    announceToScreenReader(`Showing sizes for ${currentBox} box. ${sizes.length} sizes available.`);
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // NAVIGATION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Bind navigation click handlers
   */
  function bindNavigationHandlers() {
    const picker = document.getElementById("finPickerV4");
    if (!picker) return;

    // Prevent double binding - check if already bound
    if (picker.dataset.navBound === "true") {
      log("FinPicker V4: Navigation handlers already bound, skipping");
      return;
    }
    picker.dataset.navBound = "true";

    // Handle box type clicks - use event delegation
    picker.addEventListener("click", async (e) => {
      // Block if navigation already in progress
      if (navigationInProgress) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Check if clicked element or its parent is a box pill button
      let boxPill = e.target.closest(".fpv4__pill[data-handle]");
      if (boxPill) {
        e.preventDefault();
        e.stopPropagation();
        const handle = boxPill.dataset.handle;
        const box = boxPill.dataset.box;
        log("FinPicker V4: Navigating to box", box, "handle:", handle);
        await navigateToProduct(handle, { box });
        return;
      }

      // Check if clicked element or its parent is a size button (including OOS)
      let sizeBtn = e.target.closest(".fpv4__size[data-handle]");
      if (sizeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const handle = sizeBtn.dataset.handle;
        const size = parseInt(sizeBtn.dataset.size);
        const isOOS = sizeBtn.dataset.oos === "true";

        if (isOOS) {
          // For OOS products, navigate to product page for back-in-stock subscription
          log("FinPicker V4: Navigating to OOS product for restock notification", size, "handle:", handle);
          window.location.href = `/products/${handle}`;
          return;
        }

        log("FinPicker V4: Navigating to size", size, "handle:", handle);
        await navigateToProduct(handle, { size });
        return;
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // ENHANCED LOADING STATES
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Show loading state
   * @param {Element} picker - Picker element
   */
  function showLoadingState(picker) {
    picker.classList.add("fpv4--loading");

    // Add skeleton loader
    if (!picker.querySelector(".fpv4__skeleton")) {
      const skeleton = document.createElement("div");
      skeleton.className = "fpv4__skeleton";
      skeleton.setAttribute("aria-label", "Loading product variations");
      skeleton.setAttribute("aria-live", "polite");
      skeleton.innerHTML = `
        <div class="fpv4__skeleton-line"></div>
        <div class="fpv4__skeleton-line"></div>
        <div class="fpv4__skeleton-line"></div>
      `;
      picker.appendChild(skeleton);
    }

    // Add loading state to price/stock
    const priceEl = document.getElementById("priceDisplayV4");
    if (priceEl) {
      priceEl.classList.add("fpv4__price--loading");
    }
    const stockEl = picker.querySelector(".fpv4__stock");
    if (stockEl) {
      stockEl.classList.add("fpv4__stock--loading");
    }
  }

  /**
   * Hide loading state
   * @param {Element} picker - Picker element
   */
  function hideLoadingState(picker) {
    picker.classList.remove("fpv4--loading");
    const skeleton = picker.querySelector(".fpv4__skeleton");
    if (skeleton) {
      skeleton.remove();
    }

    // Remove loading state from price/stock
    const priceEl = document.getElementById("priceDisplayV4");
    if (priceEl) {
      priceEl.classList.remove("fpv4__price--loading");
    }
    const stockEl = picker.querySelector(".fpv4__stock");
    if (stockEl) {
      stockEl.classList.remove("fpv4__stock--loading");
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // ENHANCED NAVIGATION: Partial updates + optimistic UI + caching
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Navigate to a different product variant via AJAX
   * @param {string} handle - Product handle to navigate to
   * @param {Object} options - Navigation options
   * @param {string} [options.box] - Target box type
   * @param {number} [options.size] - Target size
   * @returns {Promise<void>}
   */
  async function navigateToProduct(handle, options = {}) {
    if (!handle || navigationInProgress) {
      return;
    }

    // Cancel previous request
    if (currentController) {
      currentController.abort();
      log("FinPicker V4: Cancelled previous navigation request");
    }

    navigationInProgress = true;
    currentController = new AbortController();

    const picker = document.getElementById("finPickerV4");
    if (!picker) {
      navigationInProgress = false;
      currentController = null;
      return;
    }

    // Hide any existing errors
    hideErrorState();

    // Get target data from current picker state for optimistic update
    const currentData = getCurrentPickerData();
    const targetBox = options.box || currentData.currentBox;
    const targetSize = options.size || currentData.currentSize;

    // Show enhanced loading state
    showLoadingState(picker);

    try {
      // Check cache first
      const cached = getCachedProduct(handle);
      let html, pickerData, title;

      if (cached) {
        log("FinPicker V4: Using cached data for", handle);
        html = cached.html;
        pickerData = cached.pickerData;
        title = cached.title;
      } else {
        // Fetch from server
        const sectionId = getSectionId();
        const productUrl = `/products/${handle}`;
        const sectionUrl = `${productUrl}?section_id=${sectionId}`;

        log("FinPicker V4: Fetching", sectionUrl);

        const response = await fetch(sectionUrl, {
          signal: currentController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load product: ${response.status}`);
        }

        const responseHtml = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseHtml, "text/html");
        const newSection = doc.querySelector(`#shopify-section-${sectionId}`);

        if (!newSection) {
          throw new Error(`Product section not found`);
        }

        html = newSection.outerHTML;
        pickerData = extractPickerData(newSection);
        title = doc.querySelector("title")?.textContent;

        // Cache the result
        setCachedProduct(handle, { html, pickerData, title });
      }

      // Extract product info for optimistic update
      const productInfo = extractProductInfo(html, pickerData, targetBox, targetSize);

      // OPTIMISTIC UPDATE: Update UI immediately
      updatePickerOptimistically(productInfo.box, productInfo.size, productInfo.price, productInfo.stock);

      // Update other product elements (title, images, etc.) with smooth transition
      await updateProductElements(html, productInfo);

      // Update URL and title
      const productUrl = `/products/${handle}`;
      window.history.pushState({ path: productUrl }, "", productUrl);
      if (title) {
        document.title = title;
      }

      // Dispatch event
      document.dispatchEvent(
        new CustomEvent("product:change", {
          detail: { handle, url: productUrl },
        }),
      );

      // Announce to screen readers
      announceToScreenReader(
        `Selected ${productInfo.box} box, ${productInfo.size} centimeters. Price: ${productInfo.price}`,
      );

      log("FinPicker V4: Navigation complete");
    } catch (error) {
      // Handle abort errors gracefully
      if (error.name === "AbortError") {
        log("FinPicker V4: Navigation cancelled");
        return;
      }

      logError("FinPicker V4: Error navigating to product:", error);

      // Show user-friendly error
      showErrorState("Unable to load product. Please try again or refresh the page.", () => {
        navigateToProduct(handle, options);
      });

      // Revert optimistic update on error
      hideLoadingState(picker);

      // Fallback to full page navigation after delay
      setTimeout(() => {
        if (navigationInProgress) {
          window.location.href = `/products/${handle}`;
        }
      }, 2000);
    } finally {
      navigationInProgress = false;
      currentController = null;
      hideLoadingState(picker);
    }
  }

  /**
   * Get current picker data
   * @returns {{currentBox: string|null, currentSize: number|null}}
   */
  function getCurrentPickerData() {
    const dataEl = document.getElementById("finVariationDataV4");
    if (!dataEl) return { currentBox: null, currentSize: null };

    try {
      const data = JSON.parse(dataEl.textContent.trim());
      return {
        currentBox: data.currentBox,
        currentSize: data.currentSize,
      };
    } catch (e) {
      return { currentBox: null, currentSize: null };
    }
  }

  /**
   * Extract product info from HTML
   * @param {string} html - Section HTML
   * @param {any} pickerData - Picker data
   * @param {string} targetBox - Target box type
   * @param {number} targetSize - Target size
   * @returns {{box: string, size: number, price: string, stock: boolean}}
   */
  function extractProductInfo(html, pickerData, targetBox, targetSize) {
    // html is the section's outerHTML, create a temp container to parse it
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const section = tempDiv.firstElementChild;

    if (!section) {
      return {
        box: targetBox || pickerData?.currentBox,
        size: targetSize || pickerData?.currentSize,
        price: "",
        stock: true,
      };
    }

    // Extract price
    const priceEl = section.querySelector("#priceDisplayV4, .product-info__price, [data-price]");
    const price = priceEl?.textContent?.trim() || "";

    // Extract stock status
    const stockEl = section.querySelector(".fpv4__stock--in, .product-info__availability");
    const stock = stockEl !== null;

    return {
      box: targetBox || pickerData?.currentBox,
      size: targetSize || pickerData?.currentSize,
      price,
      stock,
    };
  }

  /**
   * Update product elements with new HTML
   * @param {string} html - New section HTML
   * @param {any} productInfo - Product info
   * @returns {Promise<void>}
   */
  async function updateProductElements(html, productInfo) {
    // html is the section's outerHTML, create a temp container to parse it
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const newSection = tempDiv.firstElementChild;

    if (!newSection) return;

    const sectionId = getSectionId();
    const currentSection = document.querySelector(`#shopify-section-${sectionId}`);

    if (!currentSection) return;

    // Track if we need to re-init the picker
    let pickerUpdated = false;

    // Only update specific elements, not entire section
    const elementsToUpdate = [
      { selector: ".product-gallery", updateFn: updateElement, isPicker: false },
      { selector: "#finPickerV4", updateFn: updatePickerElement, isPicker: true },
      { selector: '[data-block-type="price"]', updateFn: updateElement, isPicker: false },
      { selector: '[data-block-type="buy-buttons"]', updateFn: updateElement, isPicker: false },
      { selector: ".product-info__title", updateFn: updateElement, isPicker: false },
    ];

    for (const { selector, updateFn, isPicker } of elementsToUpdate) {
      const currentEl = currentSection.querySelector(selector);
      const newEl = newSection.querySelector(selector);

      if (currentEl && newEl) {
        if (isPicker) {
          // Await picker update to ensure innerHTML is set before re-init
          await updateFn(currentEl, newEl);
          pickerUpdated = true;
        } else {
          updateFn(currentEl, newEl);
        }
      }
    }

    // Re-initialize picker with new data AFTER innerHTML has been updated
    if (pickerUpdated) {
      const picker = document.getElementById("finPickerV4");
      if (picker) {
        // Reset the navBound flag to allow fresh binding check
        // (Event delegation still works, but we need to re-render content)
        log("FinPicker V4: Re-initializing picker after navigation");
        initFinPickerV4();
      }
    }
  }

  /**
   * Update picker element with smooth transition
   * Returns a Promise that resolves when the update is complete
   * @param {Element} currentEl - Current element
   * @param {Element} newEl - New element
   * @returns {Promise<void>}
   */
  function updatePickerElement(currentEl, newEl) {
    return new Promise((resolve) => {
      // Smooth transition
      currentEl.style.transition = "opacity 0.2s ease";
      currentEl.style.opacity = "0.5";

      setTimeout(() => {
        currentEl.innerHTML = newEl.innerHTML;
        currentEl.style.opacity = "1";
        resolve();
      }, 100);
    });
  }

  /**
   * Update element by replacing it
   * @param {Element} currentEl - Current element
   * @param {Element} newEl - New element
   */
  function updateElement(currentEl, newEl) {
    currentEl.replaceWith(newEl.cloneNode(true));
  }

  // Handle browser back/forward buttons
  window.addEventListener("popstate", (e) => {
    const path = window.location.pathname;
    const handle = path.split("/products/")[1]?.split("?")[0];
    if (handle) {
      navigateToProduct(handle);
    }
  });
})();
